import type { MapType } from './maps';

export interface MapRegionDefinition {
  regionKey: string;
  name: string;
}

const US_REGIONS: readonly MapRegionDefinition[] = [
  { regionKey: 'US-AL', name: 'Alabama' },
  { regionKey: 'US-AK', name: 'Alaska' },
  { regionKey: 'US-AZ', name: 'Arizona' },
  { regionKey: 'US-AR', name: 'Arkansas' },
  { regionKey: 'US-CA', name: 'California' },
  { regionKey: 'US-CO', name: 'Colorado' },
  { regionKey: 'US-CT', name: 'Connecticut' },
  { regionKey: 'US-DE', name: 'Delaware' },
  { regionKey: 'US-FL', name: 'Florida' },
  { regionKey: 'US-GA', name: 'Georgia' },
  { regionKey: 'US-HI', name: 'Hawaii' },
  { regionKey: 'US-ID', name: 'Idaho' },
  { regionKey: 'US-IL', name: 'Illinois' },
  { regionKey: 'US-IN', name: 'Indiana' },
  { regionKey: 'US-IA', name: 'Iowa' },
  { regionKey: 'US-KS', name: 'Kansas' },
  { regionKey: 'US-KY', name: 'Kentucky' },
  { regionKey: 'US-LA', name: 'Louisiana' },
  { regionKey: 'US-ME', name: 'Maine' },
  { regionKey: 'US-MD', name: 'Maryland' },
  { regionKey: 'US-MA', name: 'Massachusetts' },
  { regionKey: 'US-MI', name: 'Michigan' },
  { regionKey: 'US-MN', name: 'Minnesota' },
  { regionKey: 'US-MS', name: 'Mississippi' },
  { regionKey: 'US-MO', name: 'Missouri' },
  { regionKey: 'US-MT', name: 'Montana' },
  { regionKey: 'US-NE', name: 'Nebraska' },
  { regionKey: 'US-NV', name: 'Nevada' },
  { regionKey: 'US-NH', name: 'New Hampshire' },
  { regionKey: 'US-NJ', name: 'New Jersey' },
  { regionKey: 'US-NM', name: 'New Mexico' },
  { regionKey: 'US-NY', name: 'New York' },
  { regionKey: 'US-NC', name: 'North Carolina' },
  { regionKey: 'US-ND', name: 'North Dakota' },
  { regionKey: 'US-OH', name: 'Ohio' },
  { regionKey: 'US-OK', name: 'Oklahoma' },
  { regionKey: 'US-OR', name: 'Oregon' },
  { regionKey: 'US-PA', name: 'Pennsylvania' },
  { regionKey: 'US-RI', name: 'Rhode Island' },
  { regionKey: 'US-SC', name: 'South Carolina' },
  { regionKey: 'US-SD', name: 'South Dakota' },
  { regionKey: 'US-TN', name: 'Tennessee' },
  { regionKey: 'US-TX', name: 'Texas' },
  { regionKey: 'US-UT', name: 'Utah' },
  { regionKey: 'US-VT', name: 'Vermont' },
  { regionKey: 'US-VA', name: 'Virginia' },
  { regionKey: 'US-WA', name: 'Washington' },
  { regionKey: 'US-WV', name: 'West Virginia' },
  { regionKey: 'US-WI', name: 'Wisconsin' },
  { regionKey: 'US-WY', name: 'Wyoming' },
  { regionKey: 'US-DC', name: 'District of Columbia' },
  { regionKey: 'US-PR', name: 'Puerto Rico' },
  { regionKey: 'US-GU', name: 'Guam' },
  { regionKey: 'US-VI', name: 'U.S. Virgin Islands' },
  { regionKey: 'US-AS', name: 'American Samoa' },
  { regionKey: 'US-MP', name: 'Northern Mariana Islands' },
] as const;

const CANADA_REGIONS: readonly MapRegionDefinition[] = [
  { regionKey: 'CA-AB', name: 'Alberta' },
  { regionKey: 'CA-BC', name: 'British Columbia' },
  { regionKey: 'CA-MB', name: 'Manitoba' },
  { regionKey: 'CA-NB', name: 'New Brunswick' },
  { regionKey: 'CA-NL', name: 'Newfoundland and Labrador' },
  { regionKey: 'CA-NS', name: 'Nova Scotia' },
  { regionKey: 'CA-ON', name: 'Ontario' },
  { regionKey: 'CA-PE', name: 'Prince Edward Island' },
  { regionKey: 'CA-QC', name: 'Quebec' },
  { regionKey: 'CA-SK', name: 'Saskatchewan' },
  { regionKey: 'CA-NT', name: 'Northwest Territories' },
  { regionKey: 'CA-NU', name: 'Nunavut' },
  { regionKey: 'CA-YT', name: 'Yukon' },
] as const;

const EMEA_REGIONS: readonly MapRegionDefinition[] = [
  { regionKey: 'GBR', name: 'United Kingdom' },
  { regionKey: 'FRA', name: 'France' },
  { regionKey: 'DEU', name: 'Germany' },
  { regionKey: 'ITA', name: 'Italy' },
  { regionKey: 'ESP', name: 'Spain' },
  { regionKey: 'NLD', name: 'Netherlands' },
  { regionKey: 'BEL', name: 'Belgium' },
  { regionKey: 'CHE', name: 'Switzerland' },
  { regionKey: 'AUT', name: 'Austria' },
  { regionKey: 'SWE', name: 'Sweden' },
  { regionKey: 'NOR', name: 'Norway' },
  { regionKey: 'DNK', name: 'Denmark' },
  { regionKey: 'FIN', name: 'Finland' },
  { regionKey: 'IRL', name: 'Ireland' },
  { regionKey: 'PRT', name: 'Portugal' },
  { regionKey: 'POL', name: 'Poland' },
  { regionKey: 'CZE', name: 'Czech Republic' },
  { regionKey: 'ROU', name: 'Romania' },
  { regionKey: 'HUN', name: 'Hungary' },
  { regionKey: 'GRC', name: 'Greece' },
  { regionKey: 'ISR', name: 'Israel' },
  { regionKey: 'ARE', name: 'United Arab Emirates' },
  { regionKey: 'SAU', name: 'Saudi Arabia' },
  { regionKey: 'ZAF', name: 'South Africa' },
  { regionKey: 'NGA', name: 'Nigeria' },
  { regionKey: 'KEN', name: 'Kenya' },
  { regionKey: 'EGY', name: 'Egypt' },
  { regionKey: 'TUR', name: 'Turkey' },
] as const;

const APAC_REGIONS: readonly MapRegionDefinition[] = [
  { regionKey: 'AUS', name: 'Australia' },
  { regionKey: 'JPN', name: 'Japan' },
  { regionKey: 'CHN', name: 'China' },
  { regionKey: 'IND', name: 'India' },
  { regionKey: 'KOR', name: 'South Korea' },
  { regionKey: 'SGP', name: 'Singapore' },
  { regionKey: 'NZL', name: 'New Zealand' },
  { regionKey: 'IDN', name: 'Indonesia' },
  { regionKey: 'THA', name: 'Thailand' },
  { regionKey: 'MYS', name: 'Malaysia' },
  { regionKey: 'PHL', name: 'Philippines' },
  { regionKey: 'VNM', name: 'Vietnam' },
  { regionKey: 'TWN', name: 'Taiwan' },
  { regionKey: 'HKG', name: 'Hong Kong' },
  { regionKey: 'PAK', name: 'Pakistan' },
  { regionKey: 'BGD', name: 'Bangladesh' },
] as const;

const LATAM_REGIONS: readonly MapRegionDefinition[] = [
  { regionKey: 'MEX', name: 'Mexico' },
  { regionKey: 'BRA', name: 'Brazil' },
  { regionKey: 'ARG', name: 'Argentina' },
  { regionKey: 'COL', name: 'Colombia' },
  { regionKey: 'CHL', name: 'Chile' },
  { regionKey: 'PER', name: 'Peru' },
  { regionKey: 'VEN', name: 'Venezuela' },
  { regionKey: 'ECU', name: 'Ecuador' },
  { regionKey: 'BOL', name: 'Bolivia' },
  { regionKey: 'PRY', name: 'Paraguay' },
  { regionKey: 'URY', name: 'Uruguay' },
  { regionKey: 'GTM', name: 'Guatemala' },
  { regionKey: 'CRI', name: 'Costa Rica' },
  { regionKey: 'PAN', name: 'Panama' },
  { regionKey: 'DOM', name: 'Dominican Republic' },
  { regionKey: 'CUB', name: 'Cuba' },
  { regionKey: 'HND', name: 'Honduras' },
  { regionKey: 'SLV', name: 'El Salvador' },
  { regionKey: 'NIC', name: 'Nicaragua' },
] as const;

const REGION_MAP: Record<MapType, readonly MapRegionDefinition[]> = {
  us: US_REGIONS,
  canada: CANADA_REGIONS,
  emea: EMEA_REGIONS,
  apac: APAC_REGIONS,
  latam: LATAM_REGIONS,
};

export function getRegionDefinitions(mapType: MapType): readonly MapRegionDefinition[] {
  return REGION_MAP[mapType];
}
